// Sprint 0083 — Rule Configuration page.
// One page, tabs for: Attendance / Overtime / Certification / Grievance /
// Escalation / KPI Thresholds. Each tab = simple CRUD against its API.

import { useState } from 'react'
import {
  App, Button, DatePicker, Form, Input, InputNumber, Modal, Popconfirm, Select,
  Space, Switch, Table, Tabs, Tag,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  attendanceRulesApi, overtimeRulesApi, certificationRulesApi,
  grievanceCategoriesApi, escalationMatrixApi, kpiThresholdsApi,
} from '@/features/hr/configApi'
import { departmentsApi } from '@/features/masters/departments/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const MACHINE_TYPES = [
  'BLOW_ROOM', 'CARDING', 'COMBER', 'DRAW_FRAME_BREAKER', 'DRAW_FRAME_FINISHER',
  'LAP_FORMER', 'SIMPLEX', 'RING_FRAME', 'AUTO_CONER', 'YCP',
]
const ROLES = ['OPERATOR', 'HELPER', 'JOBBER', 'FITTER', 'ELECTRICIAN',
                'ELECTRONIC_TECHNICIAN', 'SUPERVISOR', 'SHIFT_INCHARGE']

function useDepts() {
  const { data } = useQuery({
    queryKey: ['departments-all'],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  return data?.items ?? []
}

// ── Attendance rules tab ─────────────────────────────────────────────────

function AttendanceTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const depts = useDepts()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: rows, isLoading } = useQuery({
    queryKey: ['attendance-rules'],
    queryFn: () => attendanceRulesApi.list(),
  })

  const save = useMutation({
    mutationFn: (vals: Record<string, unknown>) => attendanceRulesApi.create({
      department_id: (vals.department_id as string) || null,
      grace_period_minutes: vals.grace_period_minutes as number,
      half_day_threshold_hours: String(vals.half_day_threshold_hours),
      min_present_hours: String(vals.min_present_hours),
      auto_absent_after_consecutive_days: vals.auto_absent_after_consecutive_days as number,
      require_punch_in: !!vals.require_punch_in,
      require_punch_out: !!vals.require_punch_out,
      weekly_off_day_of_week: vals.weekly_off_day_of_week as number,
      effective_from: (vals.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
      remarks: (vals.remarks as string) || null,
    }),
    onSuccess: () => {
      message.success('Saved')
      setOpen(false)
      qc.invalidateQueries({ queryKey: ['attendance-rules'] })
      qc.invalidateQueries({ queryKey: ['hr-config-completeness'] })
    },
    onError: () => message.error('Save failed'),
  })

  return (
    <div>
      {canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => { form.resetFields(); setOpen(true) }}
          style={{ marginBottom: 12 }}>
          New Attendance Rule
        </Button>
      )}
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={rows ?? []}
        columns={[
          { title: 'Department', dataIndex: 'department_id', key: 'dept',
            render: (v: string | null) => v
              ? depts.find((d) => d.id === v)?.name ?? '—'
              : <em>All depts</em>,
          },
          { title: 'Grace (min)', dataIndex: 'grace_period_minutes', key: 'grace', width: 110 },
          { title: 'Half-day ≥ hrs', dataIndex: 'half_day_threshold_hours', key: 'hd', width: 130 },
          { title: 'Min present hrs', dataIndex: 'min_present_hours', key: 'mp', width: 130 },
          { title: 'Punch IN req', dataIndex: 'require_punch_in', key: 'pi', width: 110,
            render: (v: boolean) => v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag> },
          { title: 'Punch OUT req', dataIndex: 'require_punch_out', key: 'po', width: 110,
            render: (v: boolean) => v ? <Tag color="green">YES</Tag> : <Tag>NO</Tag> },
          { title: 'Effective from', dataIndex: 'effective_from', key: 'ef',
            render: (v: string) => dayjs(v).format('DD MMM YYYY') },
        ]}
      />
      <Modal title="New Attendance Rule" open={open} onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then((v) => save.mutate(v))}
        confirmLoading={save.isPending}>
        <Form form={form} layout="vertical" initialValues={{
          grace_period_minutes: 10, half_day_threshold_hours: 4,
          min_present_hours: 4, auto_absent_after_consecutive_days: 3,
          require_punch_in: true, require_punch_out: true, weekly_off_day_of_week: 6,
        }}>
          <Form.Item name="department_id" label="Department (blank = all)">
            <Select allowClear options={depts.map((d) => ({ label: d.name, value: d.id }))} />
          </Form.Item>
          <Form.Item name="grace_period_minutes" label="Grace period (minutes)">
            <InputNumber min={0} max={60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="half_day_threshold_hours" label="Half-day threshold (hours)">
            <InputNumber min={0} max={24} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="min_present_hours" label="Minimum hours for PRESENT">
            <InputNumber min={0} max={24} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="auto_absent_after_consecutive_days" label="Auto-absent after consecutive absent days">
            <InputNumber min={1} max={30} style={{ width: '100%' }} />
          </Form.Item>
          <Space>
            <Form.Item name="require_punch_in" label="Require IN punch" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="require_punch_out" label="Require OUT punch" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="weekly_off_day_of_week" label="Weekly off (0=Mon..6=Sun)">
            <InputNumber min={0} max={6} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

// ── Overtime tab ─────────────────────────────────────────────────────────

function OvertimeTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const depts = useDepts()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: rows, isLoading } = useQuery({
    queryKey: ['overtime-rules'],
    queryFn: () => overtimeRulesApi.list(),
  })

  const save = useMutation({
    mutationFn: (vals: Record<string, unknown>) => overtimeRulesApi.create({
      department_id: (vals.department_id as string) || null,
      normal_hours_per_day: String(vals.normal_hours_per_day),
      ot_rate_multiplier: String(vals.ot_rate_multiplier),
      weekly_off_ot_multiplier: String(vals.weekly_off_ot_multiplier),
      holiday_ot_multiplier: String(vals.holiday_ot_multiplier),
      max_ot_hours_per_day: String(vals.max_ot_hours_per_day),
      ot_grace_minutes: vals.ot_grace_minutes as number,
      requires_pre_approval: !!vals.requires_pre_approval,
      effective_from: (vals.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
    }),
    onSuccess: () => {
      message.success('Saved')
      setOpen(false)
      qc.invalidateQueries({ queryKey: ['overtime-rules'] })
      qc.invalidateQueries({ queryKey: ['hr-config-completeness'] })
    },
    onError: () => message.error('Save failed'),
  })

  return (
    <div>
      {canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => { form.resetFields(); setOpen(true) }}
          style={{ marginBottom: 12 }}>
          New Overtime Rule
        </Button>
      )}
      <Table rowKey="id" loading={isLoading} dataSource={rows ?? []} columns={[
        { title: 'Department', dataIndex: 'department_id', key: 'dept',
          render: (v: string | null) => v
            ? depts.find((d) => d.id === v)?.name ?? '—' : <em>All</em> },
        { title: 'Normal hrs/day', dataIndex: 'normal_hours_per_day' },
        { title: 'OT × normal', dataIndex: 'ot_rate_multiplier' },
        { title: 'OT × weekly-off', dataIndex: 'weekly_off_ot_multiplier' },
        { title: 'Max OT hrs/day', dataIndex: 'max_ot_hours_per_day' },
        { title: 'Pre-approval', dataIndex: 'requires_pre_approval',
          render: (v: boolean) => v ? <Tag color="orange">YES</Tag> : <Tag>NO</Tag> },
      ]} />
      <Modal title="New OT Rule" open={open} onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then((v) => save.mutate(v))}
        confirmLoading={save.isPending}>
        <Form form={form} layout="vertical" initialValues={{
          normal_hours_per_day: 8, ot_rate_multiplier: 1.5,
          weekly_off_ot_multiplier: 2, holiday_ot_multiplier: 2,
          max_ot_hours_per_day: 4, ot_grace_minutes: 15, requires_pre_approval: true,
        }}>
          <Form.Item name="department_id" label="Department (blank = all)">
            <Select allowClear options={depts.map((d) => ({ label: d.name, value: d.id }))} />
          </Form.Item>
          <Form.Item name="normal_hours_per_day" label="Normal hrs/day">
            <InputNumber min={0} max={24} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ot_rate_multiplier" label="OT rate multiplier (regular OT)">
            <InputNumber min={1} max={5} step={0.25} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="weekly_off_ot_multiplier" label="Weekly-off OT multiplier">
            <InputNumber min={1} max={5} step={0.25} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="holiday_ot_multiplier" label="Holiday OT multiplier">
            <InputNumber min={1} max={5} step={0.25} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="max_ot_hours_per_day" label="Max OT hrs/day">
            <InputNumber min={0} max={12} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="ot_grace_minutes" label="OT grace (minutes)">
            <InputNumber min={0} max={60} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="requires_pre_approval" label="Requires pre-approval" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

// ── Certification rules tab ──────────────────────────────────────────────

function CertTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: rows, isLoading } = useQuery({
    queryKey: ['cert-rules'],
    queryFn: () => certificationRulesApi.list(),
  })

  const save = useMutation({
    mutationFn: (vals: Record<string, unknown>) => certificationRulesApi.create({
      machine_type: (vals.machine_type as string) || null,
      role_required: vals.role_required as string,
      required_skill_level_min: vals.required_skill_level_min as number,
      hard_block: !!vals.hard_block,
      override_role_code: (vals.override_role_code as string) || null,
      effective_from: (vals.effective_from as dayjs.Dayjs).format('YYYY-MM-DD'),
    }),
    onSuccess: () => {
      message.success('Saved')
      setOpen(false)
      qc.invalidateQueries({ queryKey: ['cert-rules'] })
      qc.invalidateQueries({ queryKey: ['hr-config-completeness'] })
    },
    onError: () => message.error('Save failed'),
  })

  return (
    <div>
      {canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => { form.resetFields(); setOpen(true) }}
          style={{ marginBottom: 12 }}>
          New Certification Rule
        </Button>
      )}
      <Table rowKey="id" loading={isLoading} dataSource={rows ?? []} columns={[
        { title: 'Machine type', dataIndex: 'machine_type',
          render: (v: string | null) => v ? <Tag>{v}</Tag> : <em>All</em> },
        { title: 'Role', dataIndex: 'role_required',
          render: (v: string) => <Tag color="blue">{v}</Tag> },
        { title: 'Min skill', dataIndex: 'required_skill_level_min' },
        { title: 'Hard block', dataIndex: 'hard_block',
          render: (v: boolean) => v ? <Tag color="red">BLOCK</Tag> : <Tag color="orange">WARN</Tag> },
        { title: 'Override role', dataIndex: 'override_role_code',
          render: (v: string | null) => v ?? '—' },
      ]} />
      <Modal title="New Certification Rule" open={open} onCancel={() => setOpen(false)}
        onOk={() => form.validateFields().then((v) => save.mutate(v))}
        confirmLoading={save.isPending}>
        <Form form={form} layout="vertical" initialValues={{
          required_skill_level_min: 3, hard_block: false,
        }}>
          <Form.Item name="machine_type" label="Machine type (blank = all)">
            <Select allowClear options={MACHINE_TYPES.map((m) => ({ label: m, value: m }))} />
          </Form.Item>
          <Form.Item name="role_required" label="Role required" rules={[{ required: true }]}>
            <Select options={ROLES.map((r) => ({ label: r, value: r }))} />
          </Form.Item>
          <Form.Item name="required_skill_level_min" label="Min skill level (0-5)">
            <InputNumber min={0} max={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hard_block" label="Hard block uncertified?" valuePropName="checked">
            <Switch checkedChildren="BLOCK" unCheckedChildren="WARN ONLY" />
          </Form.Item>
          <Form.Item name="override_role_code" label="Override-allowed role">
            <Input placeholder="e.g. PLANT_HEAD" />
          </Form.Item>
          <Form.Item name="effective_from" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

// ── Simple-grid tabs (Grievance, Escalation, KPI) ────────────────────────

function GrievanceTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['grievance-cats'],
    queryFn: () => grievanceCategoriesApi.list(),
  })
  return (
    <Table rowKey="id" loading={isLoading} dataSource={data ?? []} columns={[
      { title: 'Code', dataIndex: 'category_code', width: 200 },
      { title: 'Name', dataIndex: 'category_name' },
      { title: 'Default assignee role', dataIndex: 'default_assignee_role' },
      { title: 'Severity', dataIndex: 'default_severity', render: (v: string) => <Tag>{v}</Tag> },
      { title: 'SLA (hrs)', dataIndex: 'target_resolution_hours' },
    ]} />
  )
}

function EscalationTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['escalation'],
    queryFn: () => escalationMatrixApi.list(),
  })
  return (
    <Table rowKey="id" loading={isLoading} dataSource={data ?? []} columns={[
      { title: 'Entity type', dataIndex: 'entity_type', render: (v: string) => <Tag color="blue">{v}</Tag> },
      { title: 'Severity', dataIndex: 'severity', render: (v: string | null) => v ?? <em>any</em> },
      { title: 'L1 after (hrs) → role', key: 'l1',
        render: (_: unknown, r: unknown) => {
          const x = r as { level_1_after_hours: number; level_1_to_role: string }
          return `${x.level_1_after_hours}h → ${x.level_1_to_role}`
        } },
      { title: 'L2 after (hrs) → role', key: 'l2',
        render: (_: unknown, r: unknown) => {
          const x = r as { level_2_after_hours: number | null; level_2_to_role: string | null }
          return x.level_2_to_role ? `${x.level_2_after_hours}h → ${x.level_2_to_role}` : '—'
        } },
      { title: 'L3 after (hrs) → role', key: 'l3',
        render: (_: unknown, r: unknown) => {
          const x = r as { level_3_after_hours: number | null; level_3_to_role: string | null }
          return x.level_3_to_role ? `${x.level_3_after_hours}h → ${x.level_3_to_role}` : '—'
        } },
    ]} />
  )
}

function KPITab() {
  const { data, isLoading } = useQuery({
    queryKey: ['kpi-thresholds'],
    queryFn: () => kpiThresholdsApi.list(),
  })
  return (
    <Table rowKey="id" loading={isLoading} dataSource={data ?? []} columns={[
      { title: 'Code', dataIndex: 'kpi_code', width: 200 },
      { title: 'Name', dataIndex: 'kpi_name' },
      { title: 'Category', dataIndex: 'category', render: (v: string | null) => v ?? '—' },
      { title: 'Green ≥', dataIndex: 'green_threshold' },
      { title: 'Yellow ≥', dataIndex: 'yellow_threshold' },
      { title: 'Red ≤', dataIndex: 'red_threshold' },
      { title: 'Direction', dataIndex: 'direction', render: (v: string) => <Tag>{v}</Tag> },
      { title: 'Unit', dataIndex: 'unit', render: (v: string | null) => v ?? '—' },
    ]} />
  )
}

export default function RuleConfigPage() {
  return (
    <div>
      <PageHeader
        title="Rule Configuration"
        subtitle="Configuration-first: every HR business rule lives in a table, not in code. Edit here, no developer needed."
      />
      <Tabs
        defaultActiveKey="attendance"
        items={[
          { key: 'attendance', label: 'Attendance', children: <AttendanceTab /> },
          { key: 'overtime', label: 'Overtime', children: <OvertimeTab /> },
          { key: 'certification', label: 'Certification', children: <CertTab /> },
          { key: 'grievance', label: 'Grievance Categories', children: <GrievanceTab /> },
          { key: 'escalation', label: 'Escalation Matrix', children: <EscalationTab /> },
          { key: 'kpi', label: 'KPI Thresholds', children: <KPITab /> },
        ]}
      />
    </div>
  )
}
